import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthMfaEnrolConfirmMutation } from '@platform/features/auth';
import { AuthField } from '../AuthField';
import { authErrorKey } from '../authErrorKey';
import type { IMfaEnrolStepProps } from './IMfaEnrolStepProps';
import { MfaKey } from './MfaKey';
import { isMfaCode, mfaCodeOf } from './mfaCode';
import { QrCode } from './QrCode';
import { useMfaSetup } from './useMfaSetup';
import './mfa.css';

/** Коди, після яких підключення треба почати знову з e-mail і пароля (квиток сплив або його немає). */
const RESTART = new Set(['mfa_enrol_expired', 'http_401', 'no_account', 'invalid_credentials', 'locked']);

/**
 * Крок «Підключіть застосунок автентифікації» (сервер відповів mfa_setup_required: адміністраторам потрібен другий фактор, а його ще не
 * підключено): пояснення, QR-код otpauth-адреси, ключ для ручного введення, поле 6-значного коду, «Підтвердити й увійти» — сервер вмикає
 * застосунок і відкриває сесію; «← Назад до входу». Квиток підключення сплив — повернення до e-mail і пароля з поясненням.
 */
export function MfaEnrolStep({ email, onDone, onRestart }: IMfaEnrolStepProps) {
  const { t } = useTranslation();
  const setup = useMfaSetup((code) => onRestart(code || 'failed'));
  const [confirm, { isLoading }] = useAuthMfaEnrolConfirmMutation();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (isLoading || !setup) return;
    if (!isMfaCode(code)) { setError(t('auth.errors.mfa_code_short')); return; }
    confirm(code).unwrap().then(onDone, (err: { code?: string }) => {
      if (RESTART.has(err.code ?? '')) onRestart(err.code === 'http_401' ? 'mfa_enrol_expired' : err.code);
      else setError(t(authErrorKey(err.code)));
    });
  };
  return (
    <form className="au__form" onSubmit={submit} noValidate>
      <div className="au__head">
        <h1>{t('auth.mfa.enrolTitle')}</h1>
        <div className="au__sub">{t('auth.mfa.enrolSub', { email })}</div>
      </div>
      <div className="au-qr__card">
        {setup ? <QrCode text={setup.uri} size={168} label={t('auth.mfa.qr')} /> : <div className="au-qr__wait">{t('auth.mfa.preparing')}</div>}
      </div>
      {setup && <MfaKey secret={setup.secret} />}
      <AuthField label={t('auth.mfa.appCode')} type="text" value={code} placeholder={t('auth.mfaCodePh')} autoComplete="one-time-code" inputMode="numeric"
        onChange={(v) => { setCode(mfaCodeOf(v)); setError(''); }} />
      {error && <div className="au__error" role="alert">{error}</div>}
      <button type="submit" className="au__submit" disabled={isLoading || !setup}>{t('auth.mfa.confirm')}</button>
      <div className="au__switch"><button type="button" className="au__linkbtn" onClick={() => onRestart()}>{t('auth.mfa.back')}</button></div>
    </form>
  );
}
